import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { toast } from 'sonner';
import {
  Flag,
  RefreshCw,
  CheckCircle,
  XCircle,
  Rocket,
  AlertTriangle,
  Skull,
  HeartPulse,
  Users,
  Eraser,
  Clock,
  Lock,
  Unlock
} from 'lucide-react';
import { featureFlagsApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import usePageTitle from '../../hooks/usePageTitle';
import HealModelSettingsCard from '../../components/admin/HealModelSettingsCard';
import ScraperSettingsCard from '../../components/admin/ScraperSettingsCard';
import { cn } from '@/lib/utils';

/*
 * Admin console for the server's feature flags (Phase 0b).
 *
 * Ported from V2's page but with one deliberate difference: there is no "Create flag" dialog.
 * On the Spring server flags are REGISTERED IN CODE (FeatureFlagKey enum) - a typo'd key must not
 * compile - and this page only edits the STATE of registered keys. A key with no row is OFF for
 * everyone; "Reset" deletes the row and returns it to that state.
 *
 * Every change takes effect within the server's cache TTL (10 s) on every node, no redeploy.
 * "Kill" is the 2am lever: forces OFF for everyone, outranking user overrides, which are KEPT so
 * "Revive" restores each user's choice.
 *
 * Aurora (2026-08-23): glass cards, semantic tones, one column on phones / two from md. The card
 * header stacks (badges under the key) below sm so the flag id never truncates to a few letters.
 */

/* Tailwind needs every class spelled out, so tones are full strings (same rule as Dashboard). */
const TONE = {
  primary: { chip: 'bg-primary/15 text-primary ring-primary/25', badge: 'border-primary/40 bg-primary/10 text-primary' },
  accent:  { chip: 'bg-accent/15 text-accent ring-accent/25', badge: 'border-accent/40 bg-accent/10 text-accent' },
  success: { chip: 'bg-success/15 text-success ring-success/25', badge: 'border-success/40 bg-success/10 text-success' },
  warning: { chip: 'bg-warning/15 text-warning ring-warning/25', badge: 'border-warning/40 bg-warning/10 text-warning' },
  danger:  { chip: 'bg-danger/15 text-danger ring-danger/25', badge: 'border-danger/40 bg-danger/10 text-danger' },
  muted:   { chip: 'bg-muted/60 text-muted-foreground ring-border', badge: 'border-border bg-muted/40 text-muted-foreground' }
};

const STATUS_CONFIG = {
  unconfigured: { label: 'No row (OFF)', color: 'muted', icon: Clock },
  off: { label: 'Off', color: 'muted', icon: XCircle },
  rolling_out: { label: 'Rolling out', color: 'primary', icon: Rocket },
  ga: { label: 'On for all', color: 'success', icon: CheckCircle },
  killed: { label: 'KILLED', color: 'danger', icon: Skull },
  sunset: { label: 'Past sunset (forced ON)', color: 'warning', icon: AlertTriangle }
};

const KIND_CONFIG = {
  UI: { label: 'UI', color: 'primary', hint: 'Presentation only. Reversible per request; users may opt in/out until the sunset date.' },
  BEHAVIOR: { label: 'BEHAVIOR', color: 'accent', hint: 'Business logic / data format. Admin only - never user-toggleable; flipping it back can strand data.' }
};

const FIELD_LABEL = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';

function errMsg(err, fallback) {
  return err?.response?.data?.error || err?.message || fallback;
}

function FlagCard({ flag, onChanged }) {
  const [rollout, setRollout] = useState(flag.rollout_percent || 0);
  const [sunset, setSunset] = useState(flag.sunset_date || '');
  const [minWorker, setMinWorker] = useState(flag.min_worker_version || '');
  const [notes, setNotes] = useState(flag.notes || '');

  // Keep local edits in sync when the server row changes underneath (another admin, a refetch).
  useEffect(() => {
    setRollout(flag.rollout_percent || 0);
    setSunset(flag.sunset_date || '');
    setMinWorker(flag.min_worker_version || '');
    setNotes(flag.notes || '');
  }, [flag.rollout_percent, flag.sunset_date, flag.min_worker_version, flag.notes]);

  const statusConfig = STATUS_CONFIG[flag.status] || STATUS_CONFIG.unconfigured;
  const kindConfig = KIND_CONFIG[flag.kind] || KIND_CONFIG.UI;
  const StatusIcon = statusConfig.icon;
  const statusTone = TONE[statusConfig.color] || TONE.muted;
  const kindTone = TONE[kindConfig.color] || TONE.primary;

  const update = useMutation({
    mutationFn: (patch) => featureFlagsApi.adminUpdate(flag.id, patch),
    onSuccess: () => { toast.success(`${flag.id} updated`); onChanged(); },
    onError: (err) => toast.error(errMsg(err, 'Update failed'))
  });
  const kill = useMutation({
    mutationFn: () => featureFlagsApi.adminKill(flag.id),
    onSuccess: () => { toast.success(`${flag.id} KILLED - off for everyone within 10 s`); onChanged(); },
    onError: (err) => toast.error(errMsg(err, 'Kill failed'))
  });
  const revive = useMutation({
    mutationFn: () => featureFlagsApi.adminRevive(flag.id),
    onSuccess: () => { toast.success(`${flag.id} revived`); onChanged(); },
    onError: (err) => toast.error(errMsg(err, 'Revive failed'))
  });
  const clearOverrides = useMutation({
    mutationFn: () => featureFlagsApi.adminClearOverrides(flag.id),
    onSuccess: (res) => { toast.success(`${res.data?.cleared ?? 0} user override(s) cleared`); onChanged(); },
    onError: (err) => toast.error(errMsg(err, 'Clear failed'))
  });
  const reset = useMutation({
    mutationFn: () => featureFlagsApi.adminDelete(flag.id),
    onSuccess: () => { toast.success(`${flag.id} reset - no row, OFF for everyone`); onChanged(); },
    onError: (err) => toast.error(errMsg(err, 'Reset failed'))
  });

  const busy = update.isPending || kill.isPending || revive.isPending || clearOverrides.isPending || reset.isPending;
  const dirtyRollout = rollout !== (flag.rollout_percent || 0);
  const dirtyMeta = sunset !== (flag.sunset_date || '') || minWorker !== (flag.min_worker_version || '') || notes !== (flag.notes || '');

  return (
    <Card
      className={cn('aurora-card min-w-0 transition-colors hover:border-primary/40', flag.killed && 'ring-1 ring-danger/60')}
      data-testid={`flag-card-${flag.id}`}
    >
      <CardHeader className="p-4 pb-3 sm:p-5 sm:pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1', kindTone.chip)}>
              <Flag className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0">
              <CardTitle className="truncate font-mono text-base text-foreground">{flag.id}</CardTitle>
              <p className="mt-0.5 text-xs text-muted-foreground text-pretty">{flag.description}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 sm:shrink-0 sm:flex-col sm:items-end">
            <Badge variant="outline" className={cn('rounded-full', statusTone.badge)}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusConfig.label}
            </Badge>
            <Badge variant="outline" className={cn('rounded-full', kindTone.badge)} title={kindConfig.hint}>
              {flag.user_toggleable ? <Unlock className="h-3 w-3 mr-1" /> : <Lock className="h-3 w-3 mr-1" />}
              {kindConfig.label}{flag.user_toggleable ? ' · user-toggleable' : ' · admin only'}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 p-4 pt-0 sm:p-5 sm:pt-0">
        {/* Global default */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <Label className="text-foreground/90">Enabled globally</Label>
            <p className="mt-1 text-xs text-muted-foreground">Default for users not selected by rollout / cohort / override.</p>
          </div>
          <Switch
            checked={!!flag.enabled_globally}
            disabled={busy}
            onCheckedChange={(v) => update.mutate({ enabled_globally: v })}
            aria-label={`Enabled globally for ${flag.id}`}
            data-testid={`flag-global-${flag.id}`}
          />
        </div>

        {/* Rollout */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label className="text-foreground/90">Rollout</Label>
            <span className="font-mono text-sm text-primary tnum">{rollout}%</span>
          </div>
          <p className="text-xs text-muted-foreground">Sticky per (user, flag). Alpha/beta testers are first in at any value above 0.</p>
          <div className="flex items-center gap-3">
            <Slider
              value={[rollout]}
              onValueChange={([v]) => setRollout(v)}
              max={100}
              step={5}
              disabled={busy}
              aria-label={`Rollout percent for ${flag.id}`}
              className="min-w-0 flex-1"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !dirtyRollout}
              onClick={() => update.mutate({ rollout_percent: rollout })}
              className="tap shrink-0 border-primary/40 text-primary hover:bg-primary/10 hover:text-primary"
            >
              Apply
            </Button>
          </div>
        </div>

        {/* Sunset / min worker / notes */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="min-w-0 space-y-1">
            <Label className={FIELD_LABEL}>Sunset date (old path removed)</Label>
            <Input
              type="date"
              value={sunset}
              onChange={(e) => setSunset(e.target.value)}
              className="h-8 border-border bg-secondary/60 text-sm"
              disabled={busy}
            />
          </div>
          {flag.kind === 'BEHAVIOR' && (
            <div className="min-w-0 space-y-1">
              <Label className={FIELD_LABEL}>Min worker version</Label>
              <Input
                value={minWorker}
                onChange={(e) => setMinWorker(e.target.value)}
                placeholder="e.g. 4.12.0"
                className="h-8 border-border bg-secondary/60 font-mono text-sm placeholder:text-muted-foreground/70"
                disabled={busy}
              />
            </div>
          )}
          <div className={cn('min-w-0 space-y-1', flag.kind === 'BEHAVIOR' && 'sm:col-span-2')}>
            <Label className={FIELD_LABEL}>Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="owner, ticket, why"
              className="h-8 border-border bg-secondary/60 text-sm placeholder:text-muted-foreground/70"
              disabled={busy}
            />
          </div>
        </div>
        {dirtyMeta && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => update.mutate({
              sunset_date: sunset || null,
              min_worker_version: flag.kind === 'BEHAVIOR' ? (minWorker || null) : undefined,
              notes: notes || null
            })}
            className="tap border-border text-foreground hover:bg-secondary/60"
          >
            Save details
          </Button>
        )}

        {/* Overrides */}
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-muted-foreground">
            <Users className="h-4 w-4 shrink-0" />
            <span className="tnum">{flag.override_count} user override{flag.override_count === 1 ? '' : 's'}</span>
            {flag.kind === 'BEHAVIOR' && <span className="text-xs text-muted-foreground/60">(ignored for BEHAVIOR)</span>}
          </span>
          {flag.override_count > 0 && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => clearOverrides.mutate()} className="tap text-foreground/90 hover:text-foreground">
              <Eraser className="h-4 w-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Kill / revive / reset */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3">
          <div className="min-w-0 text-xs text-muted-foreground tnum">
            {flag.updated_at ? <>Updated {flag.updated_at.replace('T', ' ').slice(0, 16)} by {flag.updated_by || '?'}</> : 'Never configured'}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {flag.configured && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => reset.mutate()} className="tap text-muted-foreground hover:text-foreground" title="Delete the row: back to no-row = OFF">
                Reset
              </Button>
            )}
            {flag.killed ? (
              <Button size="sm" disabled={busy} onClick={() => revive.mutate()} className="tap bg-success text-primary-foreground hover:bg-success/90" data-testid={`flag-revive-${flag.id}`}>
                <HeartPulse className="h-4 w-4 mr-1" /> Revive
              </Button>
            ) : (
              <Button size="sm" variant="destructive" disabled={busy} onClick={() => kill.mutate()} className="tap" data-testid={`flag-kill-${flag.id}`}>
                <Skull className="h-4 w-4 mr-1" /> Kill
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* First-load placeholder: two card-shaped blocks, the same grid as the real list. */
function FlagsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-busy="true" aria-label="Loading feature flags" data-testid="feature-flags-skeleton">
      {[0, 1].map((i) => (
        <div key={i} className="aurora-card p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 animate-pulse rounded-full bg-secondary/60" />
            <div className="min-w-0 flex-1">
              <div className="h-4 w-40 max-w-full animate-pulse rounded-xl bg-secondary/60" />
              <div className="mt-2 h-3 w-56 max-w-full animate-pulse rounded-xl bg-secondary/60" />
            </div>
          </div>
          <div className="mt-5 h-5 w-full animate-pulse rounded-xl bg-secondary/60" />
          <div className="mt-3 h-1.5 w-full animate-pulse rounded-full bg-secondary/60" />
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="h-8 animate-pulse rounded-xl bg-secondary/60" />
            <div className="h-8 animate-pulse rounded-xl bg-secondary/60" />
          </div>
          <div className="mt-5 h-8 w-32 animate-pulse rounded-xl bg-secondary/60" />
        </div>
      ))}
    </div>
  );
}

export default function FeatureFlagsPage() {
  usePageTitle('Feature flags');
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['adminFeatureFlags'],
    queryFn: () => featureFlagsApi.adminList().then(res => res.data),
    refetchInterval: 15000
  });

  const flags = data?.flags || [];
  const onChanged = () => {
    queryClient.invalidateQueries({ queryKey: ['adminFeatureFlags'] });
    // The user's own resolved view may have changed too.
    queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
  };

  const description = `Registered in code, configured here. Changes apply within ${Math.round((data?.cache_ttl_ms || 10000) / 1000)} s, no redeploy.`;

  if (error) {
    const forbidden = error?.response?.status === 403;
    return (
      <div data-testid="feature-flags-page">
        <PageHeader icon={Flag} title="Feature Flags" description={description} />
        <div className="aurora-card p-8 text-center sm:p-10" role="alert" data-testid="feature-flags-error">
          <span className={cn('mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full ring-1', forbidden ? TONE.danger.chip : TONE.warning.chip)}>
            <AlertTriangle className="h-6 w-6" />
          </span>
          <h3 className="mt-4">{forbidden ? 'Admin only' : 'Failed to load feature flags'}</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">{errMsg(error, '')}</p>
          {!forbidden && (
            <Button onClick={() => refetch()} className="tap mt-5 rounded-full">
              <RefreshCw className="h-4 w-4" /> Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="feature-flags-page">
      <PageHeader
        icon={Flag}
        title="Feature Flags"
        description={description}
        actions={(
          <Button variant="outline" size="sm" onClick={() => refetch()} className="tap rounded-full" data-testid="flags-refresh">
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin text-primary')} />
            Refresh
          </Button>
        )}
      />

      <HealModelSettingsCard />
      <ScraperSettingsCard />

      {isLoading ? (
        <FlagsSkeleton />
      ) : flags.length === 0 ? (
        <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10" data-testid="feature-flags-empty">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
            <Flag className="h-6 w-6" />
          </span>
          <h3 className="mt-4">No flags registered</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">Flags are added to the FeatureFlagKey enum on the server together with the change they guard.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {flags.map(flag => (
            <FlagCard key={flag.id} flag={flag} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}
